import IpdService from "../../Services/IpdService";

// ── Fetch IPD List ───────────────────────────────────────────────────────────
export const FETCH_IPD_LIST_REQUEST = "FETCH_IPD_LIST_REQUEST";
export const FETCH_IPD_LIST_SUCCESS = "FETCH_IPD_LIST_SUCCESS";
export const FETCH_IPD_LIST_FAILURE = "FETCH_IPD_LIST_FAILURE";

// ── Fetch Single IPD Admission ───────────────────────────────────────────────
export const FETCH_IPD_DETAIL_REQUEST = "FETCH_IPD_DETAIL_REQUEST";
export const FETCH_IPD_DETAIL_SUCCESS = "FETCH_IPD_DETAIL_SUCCESS";
export const FETCH_IPD_DETAIL_FAILURE = "FETCH_IPD_DETAIL_FAILURE";

// ── Add IPD Admission ────────────────────────────────────────────────────────
export const ADD_IPD_ADMISSION_REQUEST = "ADD_IPD_ADMISSION_REQUEST";
export const ADD_IPD_ADMISSION_SUCCESS = "ADD_IPD_ADMISSION_SUCCESS";
export const ADD_IPD_ADMISSION_FAILURE = "ADD_IPD_ADMISSION_FAILURE";

// ── Update IPD Admission ─────────────────────────────────────────────────────
export const UPDATE_IPD_ADMISSION_REQUEST = "UPDATE_IPD_ADMISSION_REQUEST";
export const UPDATE_IPD_ADMISSION_SUCCESS = "UPDATE_IPD_ADMISSION_SUCCESS";
export const UPDATE_IPD_ADMISSION_FAILURE = "UPDATE_IPD_ADMISSION_FAILURE";

// ── Assign Doctor (quick reassignment on the admission record) ──────────────
export const ASSIGN_IPD_DOCTOR_REQUEST = "ASSIGN_IPD_DOCTOR_REQUEST";
export const ASSIGN_IPD_DOCTOR_SUCCESS = "ASSIGN_IPD_DOCTOR_SUCCESS";
export const ASSIGN_IPD_DOCTOR_FAILURE = "ASSIGN_IPD_DOCTOR_FAILURE";

// ── Clear IPD Detail (used when unmounting detail page) ─────────────────────
export const CLEAR_IPD_DETAIL = "CLEAR_IPD_DETAIL";

// ── IPD Patient Assignment: fetch active roster for an admission ────────────
export const FETCH_ACTIVE_IPD_ASSIGNMENT_REQUEST = "FETCH_ACTIVE_IPD_ASSIGNMENT_REQUEST";
export const FETCH_ACTIVE_IPD_ASSIGNMENT_SUCCESS = "FETCH_ACTIVE_IPD_ASSIGNMENT_SUCCESS";
export const FETCH_ACTIVE_IPD_ASSIGNMENT_FAILURE = "FETCH_ACTIVE_IPD_ASSIGNMENT_FAILURE";

// ── IPD Patient Assignment: create (Assign Patient modal) ───────────────────
export const CREATE_IPD_ASSIGNMENT_REQUEST = "CREATE_IPD_ASSIGNMENT_REQUEST";
export const CREATE_IPD_ASSIGNMENT_SUCCESS = "CREATE_IPD_ASSIGNMENT_SUCCESS";
export const CREATE_IPD_ASSIGNMENT_FAILURE = "CREATE_IPD_ASSIGNMENT_FAILURE";

// ── IPD Patient Assignment: save roster edits (Manage modal) ────────────────
export const SAVE_IPD_ASSIGNMENT_ROSTER_REQUEST = "SAVE_IPD_ASSIGNMENT_ROSTER_REQUEST";
export const SAVE_IPD_ASSIGNMENT_ROSTER_SUCCESS = "SAVE_IPD_ASSIGNMENT_ROSTER_SUCCESS";
export const SAVE_IPD_ASSIGNMENT_ROSTER_FAILURE = "SAVE_IPD_ASSIGNMENT_ROSTER_FAILURE";

/**
 * Fetches the paginated IPD admission list.
 * Payload shape: { pageNo, pageSize, patientId? }
 */
export const fetchIpdList = (payload) => async (dispatch) => {
  dispatch({ type: FETCH_IPD_LIST_REQUEST });
  try {
    const response = await IpdService.getIpdList(payload);
    dispatch({ type: FETCH_IPD_LIST_SUCCESS, payload: response.data });
  } catch (error) {
    dispatch({
      type: FETCH_IPD_LIST_FAILURE,
      payload: error?.response?.data?.message || error.message,
    });
  }
};

/**
 * Fetches a single IPD admission by admissionId.
 */
export const fetchIpdDetail = (admissionId) => async (dispatch) => {
  dispatch({ type: FETCH_IPD_DETAIL_REQUEST });
  try {
    const response = await IpdService.getIpdById(admissionId);
    dispatch({ type: FETCH_IPD_DETAIL_SUCCESS, payload: response.data });
  } catch (error) {
    dispatch({
      type: FETCH_IPD_DETAIL_FAILURE,
      payload: error?.response?.data?.message || error.message,
    });
  }
};

/**
 * Creates a new IPD admission.
 * onSuccess callback is invoked after successful creation to close modal / refresh list.
 */
export const addIpdAdmission =
  (payload, onSuccess) => async (dispatch) => {
    dispatch({ type: ADD_IPD_ADMISSION_REQUEST });
    try {
      const response = await IpdService.addIpdAdmission(payload);
      dispatch({ type: ADD_IPD_ADMISSION_SUCCESS, payload: response.data });
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch({
        type: ADD_IPD_ADMISSION_FAILURE,
        payload: error?.response?.data?.message || error.message,
      });
    }
  };

/**
 * Updates an existing IPD admission.
 * refreshPayload is re-dispatched to fetchIpdList after update so the table stays in sync.
 */
export const updateIpdAdmission =
  (admissionId, payload, refreshPayload, onSuccess) => async (dispatch) => {
    dispatch({ type: UPDATE_IPD_ADMISSION_REQUEST });
    try {
      const response = await IpdService.updateIpdAdmission(admissionId, payload);
      dispatch({ type: UPDATE_IPD_ADMISSION_SUCCESS, payload: response.data });
      dispatch(fetchIpdList(refreshPayload));
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch({
        type: UPDATE_IPD_ADMISSION_FAILURE,
        payload: error?.response?.data?.message || error.message,
      });
    }
  };

/**
 * Assigns / reassigns a doctor to an IPD admission.
 * params shape: { assignedDoctorId, modifiedBy }
 */
export const assignIpdDoctor =
  (admissionId, params, refreshPayload) => async (dispatch) => {
    dispatch({ type: ASSIGN_IPD_DOCTOR_REQUEST });
    try {
      await IpdService.assignDoctor(admissionId, params);
      dispatch({ type: ASSIGN_IPD_DOCTOR_SUCCESS });
      dispatch(fetchIpdList(refreshPayload));
    } catch (error) {
      dispatch({
        type: ASSIGN_IPD_DOCTOR_FAILURE,
        payload: error?.response?.data?.message || error.message,
      });
    }
  };

/** Clears the currently loaded IPD detail from Redux state. */
export const clearIpdDetail = () => ({ type: CLEAR_IPD_DETAIL });

// ── IPD Patient Assignment thunks ────────────────────────────────────────────

const resolveNurseLabel = (id) => `Nurse #${id}`; // TODO: replace with real staff-directory lookup
const resolveDoctorLabel = (id) => `Doctor #${id}`;
const resolveShiftLabel = (id) =>
  ({ 1: "Morning", 2: "Evening", 3: "Night" }[Number(id)] || "");

const enrichAssignment = (data) =>
  data
    ? {
        ...data,
        nurseAssignments: (data.nurseAssignments || []).map((n) => ({
          ...n,
          nurseName: resolveNurseLabel(n.nurseId),
          shiftLabel: resolveShiftLabel(n.shiftId),
        })),
        dutyDoctors: (data.dutyDoctors || []).map((d) => ({
          ...d,
          doctorName: resolveDoctorLabel(d.doctorId),
          shiftLabel: resolveShiftLabel(d.shiftId),
        })),
      }
    : null;

/**
 * Fetches the active ward/nurse/duty-doctor assignment for a single admission.
 * State is keyed by admissionId so the list, Nurses column, and History modal
 * can all share one cache entry per admission.
 */
  export const fetchActiveIpdAssignment = (admissionId) => async (dispatch) => {
    dispatch({ type: FETCH_ACTIVE_IPD_ASSIGNMENT_REQUEST, admissionId });
    try {
      const response = await IpdService.getActiveIpdAssignment(admissionId);
      const enriched = enrichAssignment(response?.data);
      dispatch({
        type: FETCH_ACTIVE_IPD_ASSIGNMENT_SUCCESS,
        admissionId,
        payload: enriched,
      });
      return enriched; // <-- lets callers await the resolved value directly
    } catch (error) {
      dispatch({
        type: FETCH_ACTIVE_IPD_ASSIGNMENT_FAILURE,
        admissionId,
        payload: error?.response?.data?.message || error.message,
      });
      return null;
    }
  };

/**
 * Creates a new IPD patient assignment (Assign Patient modal).
 * Refreshes the active-assignment cache for that admission on success.
 */
export const createIpdAssignment =
  (payload, onSuccess) => async (dispatch) => {
    dispatch({ type: CREATE_IPD_ASSIGNMENT_REQUEST });
    try {
      await IpdService.assignIpdPatient(payload);
      dispatch({ type: CREATE_IPD_ASSIGNMENT_SUCCESS });
      await dispatch(fetchActiveIpdAssignment(payload.admissionId));
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch({
        type: CREATE_IPD_ASSIGNMENT_FAILURE,
        payload: error?.response?.data?.message || error.message,
      });
    }
  };

/**
 * Saves every nurse/duty-doctor row on an existing assignment (Manage modal).
 * Update endpoints are per-row, so all edited rows are pushed in parallel.
 */
export const saveIpdAssignmentRoster =
  (assignmentId, admissionId, nurseRows, doctorRows, onSuccess) =>
  async (dispatch) => {
    dispatch({ type: SAVE_IPD_ASSIGNMENT_ROSTER_REQUEST });
    try {
      await Promise.all([
        ...nurseRows.map((row) =>
          IpdService.upsertIpdNurseAssignment(assignmentId, row)
        ),
        ...doctorRows.map((row) =>
          IpdService.upsertIpdDutyDoctorAssignment(assignmentId, row)
        ),
      ]);
      dispatch({ type: SAVE_IPD_ASSIGNMENT_ROSTER_SUCCESS });
      await dispatch(fetchActiveIpdAssignment(admissionId));
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch({
        type: SAVE_IPD_ASSIGNMENT_ROSTER_FAILURE,
        payload: error?.response?.data?.message || error.message,
      });
    }
  };